import {
  DerivedDirectoryStatuses,
  DirectoryData,
  DirectoryStats,
  DirectoryTypes
} from '~/api/types/directoryData.ts';
import { ProviderNames } from '~/utils/providers/idpLabelConfiguration.tsx';
import { externalLinks } from '~/consts.ts';
import {
  ApiErrorInstance,
  isApi5xxResponseStatus,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';
import { Params } from '@solidjs/router';
import { AppContexts, getAppContext } from '~/utils/commonUtils.ts';
import {
  CUSTOMER_PORTAL_BASE_PATHS,
  CustomerPortalPath,
  DirectoryDetailsTabTypes,
  EnvironmentPagePath,
  getSlug,
  replacePathParams
} from '~/components/AppRouter/utils.ts';
import { ORGANIZATION_TAB_TYPES } from '~/pages/Organization/utils.tsx';
import { createEffect, createSignal, untrack } from 'solid-js';
import { useOrganizationContext } from '~/components/AppData/OrganizationContext.tsx';
import {
  doesDirectoryHaveUpdatedData,
  doesDirectoryHaveUpdatedGroupData,
  doesDirectoryHaveUpdatedUserData
} from '~/pages/DirectorySync/DirectoryEvents/utils.tsx';
import { DirectoryProviders } from '~/api/types/connectionData.ts';

const getTranslationKey = (translationKey: string) =>
  `directory_sync.${translationKey}`;

type IntegrationDocsForDirectory =
  | {
      [DirectoryTypes.SCIM]?: string;
    }
  | {
      [DirectoryTypes.POLL]?: string;
    };

type DirectoryProviderTemplate = {
  displayName: string;
  supportedConnectionTypes: DirectoryTypes[];
  provider: DirectoryProviders;
  integrationDocsUrl?: IntegrationDocsForDirectory;
};

const DIRECTORY_PROVIDERS_WITH_DOC_LINK = [
  DirectoryProviders.OKTA,
  DirectoryProviders.ONELOGIN,
  DirectoryProviders.GOOGLE,
  DirectoryProviders.MICROSOFT_AD,
  DirectoryProviders.JUMPCLOUD
];

const getDirectoryProviderListTemplate = (): DirectoryProviderTemplate[] => [
  {
    displayName: ProviderNames[DirectoryProviders.OKTA].label,
    supportedConnectionTypes: [DirectoryTypes.SCIM],
    provider: DirectoryProviders.OKTA,
    integrationDocsUrl: {
      [DirectoryTypes.SCIM]: externalLinks.idpIntegrationDocLinks.scim.okta
    }
  },
  {
    displayName: ProviderNames[DirectoryProviders.ONELOGIN].label,
    supportedConnectionTypes: [DirectoryTypes.SCIM],
    provider: DirectoryProviders.ONELOGIN,
    integrationDocsUrl: {
      [DirectoryTypes.SCIM]: externalLinks.idpIntegrationDocLinks.scim.onelogin
    }
  },
  {
    displayName: ProviderNames[DirectoryProviders.GOOGLE].label,
    supportedConnectionTypes: [DirectoryTypes.POLL],
    provider: DirectoryProviders.GOOGLE,
    integrationDocsUrl: {
      [DirectoryTypes.POLL]: externalLinks.idpIntegrationDocLinks.scim.google
    }
  },
  {
    displayName: ProviderNames[DirectoryProviders.MICROSOFT_AD].label,
    supportedConnectionTypes: [DirectoryTypes.SCIM],
    provider: DirectoryProviders.MICROSOFT_AD,
    integrationDocsUrl: {
      [DirectoryTypes.SCIM]:
        externalLinks.idpIntegrationDocLinks.scim.microsoftAd
    }
  },
  {
    displayName: ProviderNames[DirectoryProviders.JUMPCLOUD].label,
    supportedConnectionTypes: [DirectoryTypes.SCIM],
    provider: DirectoryProviders.JUMPCLOUD,
    integrationDocsUrl: {
      [DirectoryTypes.SCIM]: externalLinks.idpIntegrationDocLinks.scim.jumpcloud
    }
  },
  {
    displayName: ProviderNames[DirectoryProviders.PING_IDENTITY].label,
    supportedConnectionTypes: [DirectoryTypes.SCIM],
    provider: DirectoryProviders.PING_IDENTITY,
    integrationDocsUrl: {
      [DirectoryTypes.SCIM]:
        externalLinks.idpIntegrationDocLinks.scim.pingIdentity
    }
  },
  {
    displayName: ProviderNames[DirectoryProviders.CUSTOM].label,
    supportedConnectionTypes: [DirectoryTypes.SCIM],
    provider: DirectoryProviders.CUSTOM,
    integrationDocsUrl: {
      [DirectoryTypes.SCIM]: externalLinks.idpIntegrationDocLinks.scim.custom
    }
  }
];

function getDirectoryProviderDocLink(directory: DirectoryData) {
  const directoryProviderList = getDirectoryProviderListTemplate();
  const selectedDirectoryProvider = directoryProviderList.find(
    ({ provider }) => provider === directory.directoryProvider
  );
  return selectedDirectoryProvider
    ? (selectedDirectoryProvider.integrationDocsUrl?.[
        directory.directoryType
      ] ?? '')
    : '';
}

const getActiveDirectoryFromList = (
  directories: DirectoryData[]
): DirectoryData | null =>
  directories.find(
    (d) =>
      d.id &&
      (d.directoryType === DirectoryTypes.SCIM ||
        d.directoryType === DirectoryTypes.POLL)
  ) || null;

function getDirectoryListUrl({ params }: { params: Params }): string {
  const appContext = getAppContext();
  if (appContext === AppContexts.SCALEKIT_DASHBOARD) {
    return getSlug.environmentById(
      params.environmentId,
      replacePathParams(
        EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_DETAILS_OVERVIEW,
        {
          organizationId: params.organizationId,
          tabType: ORGANIZATION_TAB_TYPES.DIRECTORY_SYNC
        }
      ) as EnvironmentPagePath,
      true
    );
  } else if (appContext === AppContexts.CUSTOMER_PORTAL) {
    return getSlug.portalLink(
      params.organizationId,
      CustomerPortalPath.LIST_DIRECTORIES,
      CUSTOMER_PORTAL_BASE_PATHS.DIRECTORY_SYNC,
      true
    );
  }
  throw new Error('Invalid app context');
}

const getDerivedDirectoryStatus = (
  directory: DirectoryData
): DerivedDirectoryStatuses => {
  if (!directory.enabled) {
    return DerivedDirectoryStatuses.DISABLED;
  }
  if (!directory.lastSyncedAt) {
    return DerivedDirectoryStatuses.WAITING_TO_CONNECT;
  }
  return DerivedDirectoryStatuses.CONNECTED;
};

const getScrollableContainerId = (tabType: DirectoryDetailsTabTypes) =>
  `scrollable-container-${tabType}`;

const errorTranslationBasePath = 'server_errors.directory_sync';

const handleDirectoryCreateErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(`${errorTranslationBasePath}.create_failed`, {
      intl
    });
  }
});

const createDirectoryDataWatcher = (tabType: DirectoryDetailsTabTypes) => {
  const orgData = useOrganizationContext();

  const hasNewDataSignal = createSignal(false);
  const [directoryStats, setDirectoryStats] =
    createSignal<DirectoryStats | null>();

  let comparerFn: Function;

  switch (tabType) {
    case DirectoryDetailsTabTypes.EVENTS:
      comparerFn = doesDirectoryHaveUpdatedData;
      break;
    case DirectoryDetailsTabTypes.USERS:
      comparerFn = doesDirectoryHaveUpdatedUserData;
      break;
    case DirectoryDetailsTabTypes.GROUPS:
      comparerFn = doesDirectoryHaveUpdatedGroupData;
      break;
  }

  createEffect(() => {
    if (!comparerFn) {
      return;
    }
    if (
      directoryStats() &&
      comparerFn(directoryStats()!, orgData.selectedDirectory!.stats)
    ) {
      untrack(() => hasNewDataSignal[1](true));
    }
    untrack(() => setDirectoryStats(orgData.selectedDirectory!.stats));
  });

  return hasNewDataSignal;
};

export {
  getTranslationKey,
  getDirectoryProviderListTemplate,
  getDirectoryProviderDocLink,
  getActiveDirectoryFromList,
  getDirectoryListUrl,
  getDerivedDirectoryStatus,
  getScrollableContainerId,
  handleDirectoryCreateErrors,
  createDirectoryDataWatcher,
  DIRECTORY_PROVIDERS_WITH_DOC_LINK
};
